import type { SpagoHandmadeConnector, SpagoIntegrationStatus } from '../services/api';

/**
 * The tool name the backend registers for a connector name. Port of
 * `_derive_connector_fn_name` in back/agents/connector_tools.py — keep in step.
 * `u` flag: Python's re replaces per code point, so an emoji must become ONE `_`
 * (without `u`, JS would see two UTF-16 units and write two).
 */
export function toToolName(raw: string | null | undefined): string {
  return (raw ?? '').toLowerCase().trim().replace(/[^a-z0-9_]/gu, '_').replace(/^_+|_+$/g, '') || 'connector_tool';
}

export interface FlaggedHandmadeConnector extends SpagoHandmadeConnector {
  /** Registers under the same tool name as a package tool — the duplicate to remove. */
  clashesWithPackage: boolean;
}

/**
 * The hand-made connectors the Spago card's notice lists: ACTIVE ones only
 * (turning one off removes it, and with it its clash flag), with any that
 * registers under the same TOOL NAME as a package tool flagged and sorted first.
 * Names are compared through `toToolName`, not as raw strings: `Spago_get_my_orders`
 * and `spago-get-my-orders` both register as `spago_get_my_orders` and really
 * collide. The backend prefers the package row on a clash, but the owner should
 * still see and remove the duplicate.
 */
export function activeHandmadeConnectors(
  status: Pick<SpagoIntegrationStatus, 'connectors' | 'handmade_connectors'>,
): FlaggedHandmadeConnector[] {
  const packageNames = new Set(status.connectors.map((c) => toToolName(c.name)));
  const flagged = status.handmade_connectors
    .filter((h) => h.is_active)
    .map((h) => ({ ...h, clashesWithPackage: packageNames.has(toToolName(h.name)) }));
  // Stable sort: clashes first, otherwise the server's order is kept.
  return flagged.sort((a, b) => Number(b.clashesWithPackage) - Number(a.clashesWithPackage));
}
